import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { BASEURL } from "../../../../../constants";

const MatchColumn = ({ setCurrentSlideData, contentId, data }) => {
  const [questions, setQuestions] = useState(
    Array(4).fill(null).map((_, index) => ({
      id: (index + 1).toString(),
      text: "",
      correctAnswer: null,
    }))
  );

  const [answers, setAnswers] = useState(
    Array(4).fill(null).map((_, index) => ({
      id: (index + 1).toString(),
      image: "",
      imageKey: "", // Initialize with empty imageKey
    }))
  );

  const fileInputs = useRef([]);

  // Fetch images for answers if imageKeys exist
  useEffect(() => {
    async function fetchAnswerImages(data) {
      if (!data.answers) return;

      const imagesPromiseArray = [];
      const indexArray = [];

      data.answers.forEach((answer, index) => {
        if (answer.imageKey && answer.imageKey.trim() !== "") { // Check for non-empty imageKey
          const imagePromise = axios.get(`${BASEURL}/api/get-image`, {
            params: { imageId: answer.imageKey },
          });
          imagesPromiseArray.push(imagePromise);
          indexArray.push(index);
        }
      });

      try {
        const promiseResponse = await Promise.all(imagesPromiseArray);
        const updatedAnswers = [...data.answers];

        promiseResponse.forEach((resObj, i) => {
          const replaceIndex = indexArray[i];
          updatedAnswers[replaceIndex] = {
            ...updatedAnswers[replaceIndex],
            image: resObj.data.dataUrl,
          };
        });

        setAnswers(updatedAnswers);
      } catch (error) {
        console.error("Error fetching answer images:", error);
      }
    }

    if (data) {
      setQuestions(data.questions || questions);
      setAnswers(data.answers || answers);
      fetchAnswerImages(data);
    }
  }, []);

  // Update slide data when questions/answers change
  useEffect(() => {
    setCurrentSlideData((currentSlideData) => ({
      ...currentSlideData,
      content: currentSlideData.content.map((contentObj) =>
        contentObj.id === contentId
          ? { ...contentObj, data: { questions: questions, answers: answers } }
          : contentObj
      ),
    }));
  }, [questions, answers]);

  const handleFileChange = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const uniqueId = uuidv4();
    const formData = new FormData();
    formData.append("image", file);
    formData.append("id", uniqueId);

    try {
      await axios.post(`${BASEURL}/api/upload-image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const imageResponse = await axios.get(`${BASEURL}/api/get-image`, {
        params: { imageId: uniqueId },
      });

      const newAnswers = [...answers];
      newAnswers[index] = {
        ...answers[index],
        image: imageResponse.data.dataUrl,
        imageKey: uniqueId,
      };
      setAnswers(newAnswers);
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  const handleDeleteImage = (index, imageKey) => {
    if (imageKey) {
      axios
        .delete(`${BASEURL}/api/delete-image`, { data: { key: imageKey } })
        .then(() => {
          const newAnswers = [...answers];
          newAnswers[index] = { ...answers[index], image: "", imageKey: "" };
          setAnswers(newAnswers);
        })
        .catch((error) => {
          console.error("Error deleting image:", error);
        });
    }
  };

  return (
    <div className="font-sans">
      <h1 className="text-2xl font-bold mb-4">Match the Questions & Answers</h1>
      {questions.map((q, index) => (
        <div key={index} className="flex items-start space-x-16 mb-4">
          <div className="flex-1">
            <label className="block font-medium">Question {index + 1}:</label>
            <input
              type="text"
              value={q.text}
              onChange={(e) => {
                const newQuestions = [...questions];
                newQuestions[index].text = e.target.value;
                setQuestions(newQuestions);
              }}
              placeholder="Enter question"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="flex-1 relative">
            <label className="block font-medium">Answer {index + 1}:</label>
            {!answers[index].image ? (
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, index)}
                className="w-full p-1 border border-gray-300 rounded-md"
              />
            ) : (
              <div className="relative w-24 h-24">
                <img
                  src={answers[index].image}
                  alt="Uploaded"
                  className="mt-2 w-24 h-24 object-cover rounded-md border border-gray-300"
                />
                <button
                  onClick={() => handleDeleteImage(index, answers[index].imageKey)}
                  className="absolute top-[-7px] right-[-7px] bg-red-500 text-white rounded-full p-1 text-xs"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MatchColumn;